import { useEffect, useRef, useState } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { toast } from "sonner";
import { api, API_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import AuthDialog from "@/components/AuthDialog";
import CheckoutDialog from "@/components/CheckoutDialog";
import PhotoUploadDialog from "@/components/PhotoUploadDialog";

const GOLD = "#C5A880";
const MINE = "#8FA4B5";
const CREAM = 0xffe9eff3; // #F3EFE9 in AABBGGRR (little-endian)
const WHITE = 0xffffffff;

export default function GridPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [grid, setGrid] = useState(null);
  const [maps, setMaps] = useState(null);
  const [mine, setMine] = useState([]);
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [uploadPixel, setUploadPixel] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/grid");
      setGrid(data);
    } catch {
      toast.error(t("common.error"));
    }
  };

  const loadMine = async () => {
    if (!user) {
      setMine([]);
      return;
    }
    try {
      const { data } = await api.get("/pixels/mine");
      setMine(data.pixels.map((p) => p.index));
    } catch {
      setMine([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Carica la maschera della sagoma e costruisce le mappe cella<->indice
  useEffect(() => {
    if (!grid) return;
    setMaps(null);
    const img = new Image();
    // The mask is served by the API on a different origin in Render.
    // Request it with CORS so its pixels can be read from the canvas.
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const w = grid.width;
      const h = grid.height;
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      const cx = c.getContext("2d", { willReadFrequently: true });
      cx.drawImage(img, 0, 0, w, h);
      const data = cx.getImageData(0, 0, w, h).data;
      const cellToIdx = new Int32Array(w * h).fill(-1);
      const idxToPos = new Int32Array(grid.total * 2);
      const base = new Uint32Array(w * h);
      let k = 0;
      for (let i = 0; i < w * h; i++) {
        if (data[i * 4] > 127) {
          cellToIdx[i] = k;
          idxToPos[k * 2] = i % w;
          idxToPos[k * 2 + 1] = (i / w) | 0;
          base[i] = CREAM;
          k++;
        } else {
          base[i] = WHITE;
        }
      }
      const baseCanvas = document.createElement("canvas");
      baseCanvas.width = w;
      baseCanvas.height = h;
      const bctx = baseCanvas.getContext("2d");
      const id = bctx.createImageData(w, h);
      new Uint32Array(id.data.buffer).set(base);
      bctx.putImageData(id, 0, 0);
      setMaps({ cellToIdx, idxToPos, baseCanvas });
    };
    img.src = `${API_URL}/grid/mask.png`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid?.width, grid?.height]);

  useEffect(() => {
    if (!grid || !maps || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(maps.baseCanvas, 0, 0);
    const mineSet = new Set(mine);
    ctx.fillStyle = GOLD;
    for (const i of grid.sold_indices) {
      if (i >= grid.total || mineSet.has(i)) continue;
      ctx.fillRect(maps.idxToPos[i * 2], maps.idxToPos[i * 2 + 1], 1, 1);
    }
    ctx.fillStyle = MINE;
    for (const i of mine) {
      if (i >= grid.total) continue;
      ctx.fillRect(maps.idxToPos[i * 2], maps.idxToPos[i * 2 + 1], 1, 1);
    }
  }, [grid, mine, maps]);

  const pixelFromEvent = (e) => {
    if (!grid || !maps || !canvasRef.current) return null;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.floor(((e.clientX - rect.left) / rect.width) * grid.width);
    const y = Math.floor(((e.clientY - rect.top) / rect.height) * grid.height);
    if (x < 0 || y < 0 || x >= grid.width || y >= grid.height) return null;
    const index = maps.cellToIdx[y * grid.width + x];
    if (index < 0) return null; // sfondo: nessun pixel qui
    return { x, y, index };
  };

  const handleMove = (e) => {
    const p = pixelFromEvent(e);
    if (!p) {
      setHover(null);
      return;
    }
    const mineSet = new Set(mine);
    const sold = grid.sold_indices.includes(p.index);
    setHover({
      ...p,
      status: mineSet.has(p.index) ? "mine" : sold ? "sold" : "available",
      cx: e.clientX,
      cy: e.clientY,
    });
  };

  const handleClick = (e) => {
    const p = pixelFromEvent(e);
    if (!p) return;
    if (grid.sold_indices.includes(p.index)) return;
    setSelected({ index: p.index, x: p.x, y: p.y });
    if (!user) {
      setAuthOpen(true);
      return;
    }
    setCheckoutOpen(true);
  };

  const statusLabel = (s) =>
    s === "mine" ? t("grid.yours") : s === "sold" ? t("grid.sold") : t("grid.available");

  return (
    <main className="min-h-screen bg-[#FAF8F5] pt-[57px] flex flex-col" data-testid="grid-page">
      <div className="px-5 md:px-12 py-8 border-b border-border/60 flex flex-wrap items-end justify-between gap-6">
        <div>
          <h1 className="font-display font-black tracking-tighter text-4xl md:text-5xl" data-testid="grid-title">
            {t("grid.title")}
          </h1>
          <p className="mt-2 text-[10px] tracking-[0.3em] text-muted-foreground">{t("grid.sub").toUpperCase()}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] tracking-[0.25em] text-golddark" data-testid="grid-hint">
            {t("grid.clickToBuy").toUpperCase()}
          </p>
          <p className="mt-1 text-[10px] tracking-[0.25em] text-muted-foreground">{t("grid.zoom").toUpperCase()}</p>
        </div>
      </div>

      <div ref={containerRef} data-lenis-prevent className="relative flex-1 min-h-[70vh] bg-[#FAF8F5] overflow-hidden">
        {(!grid || !maps) && (
          <div className="absolute inset-0 flex items-center justify-center text-xs tracking-[0.3em] text-muted-foreground" data-testid="grid-loading">
            {t("grid.loading").toUpperCase()}
          </div>
        )}
        <TransformWrapper
          minScale={0.6}
          maxScale={30}
          initialScale={1}
          centerOnInit
          wheel={{ step: 0.12 }}
          doubleClick={{ mode: "zoomIn" }}
          panning={{ velocityDisabled: true }}
        >
          <TransformComponent wrapperClass="!w-full !h-full" contentClass="!w-full">
            <canvas
              ref={canvasRef}
              width={grid?.width || 1766}
              height={grid?.height || 883}
              data-testid="pixel-canvas"
              className="w-full block cursor-crosshair border border-border/40"
              onMouseMove={handleMove}
              onMouseLeave={() => setHover(null)}
              onClick={handleClick}
            />
          </TransformComponent>
        </TransformWrapper>

        {hover && (
          <div
            className="pixel-tooltip fixed z-40 bg-[#FFFFFF]/90 border border-border px-4 py-3"
            style={{ left: hover.cx + 16, top: hover.cy + 16 }}
            data-testid="pixel-tooltip"
          >
            <div className="text-[9px] tracking-[0.3em] text-muted-foreground">{t("grid.coords")}</div>
            <div className="font-mono text-lg text-[#3C3735] tabular-nums">
              [{hover.x}, {hover.y}]
            </div>
            <div
              className={`mt-1 text-[9px] tracking-[0.3em] ${
                hover.status === "available" ? "text-golddark" : hover.status === "mine" ? "text-[#8FA4B5]" : "text-muted-foreground"
              }`}
              data-testid="pixel-tooltip-status"
            >
              {statusLabel(hover.status)}
            </div>
          </div>
        )}
      </div>

      <AuthDialog
        open={authOpen}
        onOpenChange={setAuthOpen}
        onSuccess={() => {
          if (selected != null) setCheckoutOpen(true);
        }}
      />
      <CheckoutDialog
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        requestedIndex={selected?.index ?? null}
        requestedPos={selected ? { x: selected.x, y: selected.y } : null}
        onSuccess={(indices) => {
          load();
          loadMine();
          if (indices && indices.length > 0) setUploadPixel(indices[0]);
        }}
      />
      <PhotoUploadDialog
        open={uploadPixel != null}
        onOpenChange={(o) => !o && setUploadPixel(null)}
        pixelIndex={uploadPixel}
        onDone={() => {
          setUploadPixel(null);
          toast.success(t("upload.success"));
        }}
      />
    </main>
  );
}
